import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import logo from './wysupp-green-transparent_1.svg';
import backgroundImage from './background_img.jpg';
import { FaUser, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from './AuthContext'; // Import the useAuth hook

export default function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);
    const [captchaDone, setCaptchaDone] = useState(false);

    let recaptchaRef = useRef(null);
    const { login, accessToken } = useAuth(); // Get login function and accessToken from context
    const navigate = useNavigate();
    // A one-off message from the page that sent the user here, e.g. once 2FA is set up.
    const notice = useLocation().state?.notice;

    // Redirects if the user is already logged in (accessToken becomes available)
    useEffect(() => {
        if (accessToken) {
            navigate('/home');
        }
    }, [accessToken, navigate]);

    useEffect(() => {
        const data = localStorage.getItem('login_attempts');
        if (data !== null) setLoginAttempts(JSON.parse(data));
    }, []);

    const onChange = (value) => {
        setCaptchaDone(!!value);
    };

    const validateForm = () => {
        if (!username || !password) {
            setError('Username and password are required');
            return false;
        }
        setError('');
        return true;
    };

    const forgot_password = () => {
        navigate('/forgot-password');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        setError('');
    
        try {
            // --- FIX APPLIED HERE: We now check the result object from the login function ---
            const result = await login({ username, password });
            
            if (result?.requires2faSetup) {
                localStorage.setItem('username', username); // The 2fa page reads this
                navigate('/2fa-setup');
            } else if (result?.requires2fa) {
                localStorage.setItem('login_username_for_otp', username);
                navigate('/otp-verification');
            } else if (result?.requiresPasswordChange) {
                // The AuthContext handled the state, we just need to navigate
                navigate('/change-password');
            }
            // On successful normal login, the useEffect will handle the redirect.
            
        } catch (error) {
            // The catch block no longer needs to handle the 403 error specifically.
            const errorData = error.response?.data;
            let errorMessage = 'An unknown error occurred.';
    
            if (typeof errorData?.detail === 'string') {
                errorMessage = errorData.detail;
            } else if (Array.isArray(errorData?.detail)) {
                errorMessage = errorData.detail[0]?.msg || 'Invalid input provided.';
            }
            setError(errorMessage);
    
            const newAttempts = loginAttempts + 1;
            setLoginAttempts(newAttempts);
            localStorage.setItem('login_attempts', JSON.stringify(newAttempts));
            if (newAttempts >= 3 && recaptchaRef.current) {
                recaptchaRef.current.reset();
                setCaptchaDone(false);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page-container">
            <div className="login-image-section" style={{ backgroundImage: `url(${backgroundImage})` }}>
                <div className="login-image-overlay"></div>
                <div className="login-logo-container">
                    <img src={logo} alt="Wysupp Logo" className="login-logo" />
                </div>
            </div>
            <div className="login-form-section">
                <div className="login-form-wrapper">
                    <h1 className="login-title">Welcome</h1>
                    <p className="login-subtitle">Please, Log in to continue</p>

                    {notice && !error && <p className="login-notice">{notice}</p>}
                    {error && <p className="login-errmsg">{error}</p>}

                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="login-input-group">
                            <FaUser className="login-input-icon" />
                            <input
                                type="text"
                                id="username"
                                autoComplete="off"
                                placeholder='Username'
                                onChange={(e) => setUsername(e.target.value)}
                                value={username}
                                required
                            />
                        </div>
                        <div className="login-input-group">
                            <FaLock className="login-input-icon" />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                placeholder='Password'
                                onChange={(e) => setPassword(e.target.value)}
                                value={password}
                                required
                            />
                            {showPassword ? (
                                <FaEyeSlash className="password-toggle-icon" onClick={() => setShowPassword(false)} />
                            ) : (
                                <FaEye className="password-toggle-icon" onClick={() => setShowPassword(true)} />
                            )}
                        </div>

                        <a href="#" onClick={forgot_password} className="forgot-password-link">Forgot your password?</a>

                        {loginAttempts >= 3 && (
                            <div
                                className="recaptcha-container"
                                style={{ display: loginAttempts >= 3 ? 'flex' : 'none' }}
                            >
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                    onChange={onChange}
                                    theme="dark"
                                />
                            </div>
                        )}

                        <button
                            className="login-button"
                            disabled={loading || (loginAttempts >= 3 && !captchaDone)}
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>
                    </form>
                </div>
            </div>
            <style>{`
            /* FIX: This rule ensures padding and borders don't expand elements, preventing scrollbars and alignment issues. */
            .login-page-container, .login-page-container * {
                box-sizing: border-box;
            }
            
            :root {
                --login-bg: #1a202c;
                --login-form-bg: #2d3748;
                --login-text: #e2e8f0;
                --login-light-text: #a0aec0;
                --login-green-accent: #48bb78;
                --login-input-bg: #1a202c;
                --login-border-color: #4a5568;
            }

            .login-page-container {
                display: grid;
                grid-template-columns: 1.2fr 1fr;
                height: 100vh;
                font-family: 'Inter', sans-serif;
                background-color: var(--login-bg);
            }

            /* --- Image Section (Left Side) --- */
            .login-image-section {
                background-size: cover;
                background-position: center;
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 65vw;
            }

            .login-image-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(26, 32, 44, 0.6); /* Dark overlay */
            }

            .login-logo-container {
                position: relative;
                z-index: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 1.5rem;
            }

            .login-logo {
                width: 400px;
                height: 400px;
            }

            .login-logo-text {
                font-size: 2.5rem;
                font-weight: 700;
                color: #ffffff;
                letter-spacing: 1px;
            }

            /* --- Form Section (Right Side) --- */
            .login-form-section {
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: var(--login-form-bg);
                padding: 2rem;
                width: 35vw;
                border-left: 5px solid #16362e;
            }

            .login-form-wrapper {
                width: 100%;
                max-width: 400px;
            }

            .login-title {
                font-size: 2.5rem;
                font-weight: 800;
                color: var(--login-text);
                margin-bottom: 0.5rem;
            }

            .login-subtitle {
                font-size: 1rem;
                color: var(--login-light-text);
                margin-bottom: 2.5rem;
            }

            .login-notice {
                background-color: rgba(52, 211, 153, 0.1);
                color: #a7f3d0;
                border: 1px solid rgba(52, 211, 153, 0.3);
                padding: 0.75rem;
                border-radius: 6px;
                margin-bottom: 1.5rem;
                font-size: 0.9rem;
                text-align: center;
            }

            .login-errmsg {
                background-color: rgba(239, 68, 68, 0.1);
                color: #fca5a5;
                border: 1px solid rgba(239, 68, 68, 0.3);
                padding: 0.75rem;
                border-radius: 6px;
                margin-bottom: 1.5rem;
                font-size: 0.9rem;
                text-align: center;
            }

            .login-form {
                display: flex;
                flex-direction: column;
                gap: 1.25rem;
            }

            .login-input-group {
                position: relative;
            }

            .login-input-group input {
                width: 100%;
                height: 2.5rem;
                padding: 1rem 1rem 1rem 3rem; /* Space for the icon */
                background-color: var(--login-input-bg);
                border: 1px solid var(--login-border-color);
                border-radius: 6px;
                color: var(--login-text);
                font-size: 1rem;
                transition: border-color 0.2s;
            }

            .login-input-group input:focus {
                outline: none;
                border-color: var(--login-green-accent);
            }

            .login-input-icon {
                position: absolute;
                top: 50%;
                left: 1rem;
                transform: translateY(-50%);
                color: var(--login-light-text);
            }

            .password-toggle-icon {
                position: absolute;
                top: 50%;
                right: 1rem;
                transform: translateY(-50%);
                color: var(--login-light-text);
                cursor: pointer;
            }

            .forgot-password-link {
                font-size: 0.85rem;
                color: var(--login-light-text);
                text-align: right;
                text-decoration: none;
                transition: color 0.2s;
                align-self: flex-end; /* FIX: Prevents the link from stretching full width */
            }

            .forgot-password-link:hover {
                color: var(--login-green-accent);
            }

            .recaptcha-container {
                display: flex;
                justify-content: center;
            }

            .login-button {
                background-color: var(--login-green-accent);
                color: var(--login-bg);
                border: none;
                border-radius: 6px;
                padding: 1rem;
                font-size: 1rem;
                font-weight: 600;
                cursor: pointer;
                transition: background-color 0.2s;
                margin-top: 0.5rem;
            }

            .login-button:hover {
                background-color: #38a169;
            }

            .login-button:disabled {
                background-color: #4a5568;
                cursor: not-allowed;
            }

            .login-input-group input:-webkit-autofill ~ .password-toggle-icon,
            .login-input-group input:autofill ~ .password-toggle-icon {
              color: var(--dark-bg); /* Change to a dark color */
            }

            /* Optional: Adjust hover color when autofilled */
            .login-input-group input:-webkit-autofill ~ .password-toggle-icon:hover,
            .login-input-group input:autofill ~ .password-toggle-icon:hover {
              color: var(--card-bg); /* Use a slightly lighter dark color on hover */
            }

            /* --- Responsive Design --- */
            @media (max-width: 900px) {
                .login-page-container {
                    grid-template-columns: 1fr;
                }
                .login-image-section {
                    display: none; /* Hide image on smaller screens */
                }
                .login-form-section {
                    width: 100vw;
                    background: var(--login-bg);
                    border-left: none;
                }
            }
            `}</style>
        </div>
    );
}